import { enqueueSnackbar } from "notistack"
import { api } from "../../../../api"
import { setAlertFunc } from "../../../../components/AlertToaster/AlertSlice"
import { DropdownObj, HistoryData, IFile, IResponse, ProjectCategory, tagsDetail } from "../../../Common/commonTypes"
import { getDemolition, getDemolitionTabs } from "../../OperationSlice"
import { SurveyContractor } from "../Survey/SurveyService"

const MODULE_URL = "operations/demolitions"
const MODULE_URL_V3 = "demolitions"

export interface IDemolitionOverallProgress {
    project_id: number
    project_name: string
    display_id: string
    deal_type: string
    template_id: number
    template_name: string
    site_id: number
    site_name: string
    progress: number
    start_date: string | null
    requested_closure_date: string | null
    closure_date: string | null
    actual_close_date: string | null
    tat_days: number | null
    age_days: number | null
    business_poc: { id: number; name: string } | null
    status: {
        id: number
        label: string
        color: string
        text_color: string
    }
    on_hold: {
        on_hold: boolean
        reason?: string
        standby?: string
        tentative_date?: string
    }
    tags: {
        primary: tagsDetail[]
        others: tagsDetail[]
    }
    finance_rejected: boolean
    finance_reject_reason: string | null
    finance_ticket_id: number | null
}

export interface IDemolitionTicketDetailsV3 {
    project_id: number
    display_id: string
    ticket_id: number
    ticket_display_id: string
    demolition_reason: { id: number; reason: string } | null
    is_site_demolition_req: boolean
    pickup_date: string | null
    dismantling_estimated_date: string | null
    cancellation: {
        required: boolean
        status: string | null
        date: string | null
    }
    security_deposit: {
        amount: number | null
        refund_status: string | null
        refund_date: string | null
    }
    source_site: {
        id: number
        name: string
        address: string
        city: string
        state: string
        pincode: string
        latitude: number | null
        longitude: number | null
    } | null
    destination_site: {
        id: number
        name: string
        address: string
        city: string
        state: string
        pincode: string
        latitude: number | null
        longitude: number | null
    } | null
    materials: {
        total: number
        chargers: Array<{
            usage_id: number
            plp_id: number
            serial_number: string | null
            quantity: number
            charger_name: string
            sku_code: string
            description: string | null
            operational_status: string | null
            operational_status_color: string | null
        }>
        items: Array<{
            usage_id: number
            plp_id: number
            serial_number: string | null
            quantity: number
            charger_name: string
            sku_code: string
            description: string | null
            operational_status: string | null
            operational_status_color: string | null
        }>
    }
}

export type DemolitionParentStatus = "all_tab" | "demo_open" | "demo_in_progress" | "demo_completed" | "demo_dropped"

export interface IDemolitionTabCount {
    parent_status_id: DemolitionParentStatus
    status: string
    ticket_count: number
    status_color: string
    status_text_color: string
    child_status_id?: number[]
    sub_counts: {
        all: number
        cpo: number
        outright: number
    } | null
}

export interface IDemolitionListFilters {
    page?: number
    per_page?: number
    search_key?: string
    status_ids?: string
    source_site_ids?: string
    raised_by?: number
    raised_on_start?: string
    raised_on_end?: string
    updated_on_start?: string
    updated_on_end?: string
    age?: string
    age_op?: string
    target_closure_start?: string
    target_closure_end?: string
    demolition_reason_ids?: string
    tag_ids?: string
    category_ids?: string
    assigned_to?: number
    deal_type?: "Outright" | "CPO"
    on_hold?: string
    on_hold_date?: string
    on_hold_reason_ids?: string
}

export interface IDemolitionListSite {
    id: number | null
    name: string | null
    location: string | null
    address: string | null
}

export interface IDemolitionListStatus {
    id: number
    label: string
    color: string | null
    text_color: string | null
    parent_status_id: number | null
}

export interface IDemolitionListTag {
    id: number
    name: string
    color: string
    text_color: string
}

export interface IDemolitionListRow {
    project_id: number
    project_display_id: string | null
    project_name: string | null
    ticket_id: number
    ticket_display_id: string | null
    template_name: string | null
    deal_type: string | null
    category: ProjectCategory
    status: IDemolitionListStatus
    progress: number
    source_site: IDemolitionListSite
    destination_site: IDemolitionListSite
    demolition_reason: { id: number | null; reason: string | null } | null
    is_site_demolition_req: boolean
    charger_details: { count: number } | null
    tags: { primary: IDemolitionListTag[]; others: IDemolitionListTag[] }
    assigned_to: { id: number | null; name: string | null }
    business_poc_name: string | null
    raised_by: string | null
    raised_on: string | null
    updated_on: string | null
    age: string
    on_hold_tat: string
    tat: string | null
    target_completion_date: string | null
    flags: { is_unread: boolean | null }
    on_hold: { on_hold: boolean; reason?: string; standby?: string; tentative_date?: string } | null
}

export interface IDemolitionListMeta {
    request_id: string
    parent_status: string
    page: number
    per_page: number
    total: number
    total_pages: number
    filters_applied: Record<string, any>
}

export interface IDemolitionListResponse {
    meta: IDemolitionListMeta
    rows: IDemolitionListRow[]
}

export interface IRaiseDemolitionPayload {
    template_id: number
    deal_type: "Outright" | "CPO"
    project_name: string
    deal_done_by_id: number
    source_site_id: number
    destination_site_id: number
    reason_id: number
    project_closure_date?: string
    is_site_demolition_req?: boolean
    assigned_to?: number
    accounts?: {
        account_id: number
        account_type_id: number
        billing_address_id: number
        contact_id: number
        contact_type_id: number
    }[]
    po_number?: string
    program_id?: number
    category_id?: number
    total_amount?: number
    payment_terms?: number
    payment_terms_obj?: any[]
    on_ground_poc_name?: string
    on_ground_poc_phone?: string
    on_ground_poc_email?: string
    plp_ids?: number[]
}

export interface IRaiseDemolitionResponse {
    project_id: number
    ticket_id: number
    display_id: string
    finance_id: number | null
    demo_ptm_id: number
}

export interface IDemolitionSiteMaterialItem {
    plp_id: number
    project_id?: number | null
    serial_number: string | null
    quantity: number
    display_name: string
    sku_code: string
    description: string | null
    operational_status_id: number | null
    operational_status: string | null
    charger_sub_status: string | null
    charger_sub_status_color: string | null
}

export interface IDemolitionSiteMaterialsResponse {
    demo_ptm_id?: number
    chargers: IDemolitionSiteMaterialItem[]
    materials: IDemolitionSiteMaterialItem[]
}

export interface IDemolitionProfileSiteDetail {
    site_id: number
    site_id_str: string
    site_name: string
    address: string
    city: string
    state: string
    pincode: string
    country: string
    country_id: number
    latitude: number | null
    longitude: number | null
    map_link: string | null
}

export interface IDemolitionProfileAccount {
    account_id: number
    account_type_id: number
    account_name: string
    account_type: string
    billing_address_id: number
    contacts: any[]
    validity_from: string | null
    validity_to: string | null
}

export interface IDemolitionProfileOverviewResponse {
    project_id: number
    data: {
        common: {
            project: {
                project_id: number
                project_name: string
                display_id: string
                created_at: string
                deal_type: string
                template_id: number
                template_name: string
                source_site_id: number
                destination_site_id: number
            }
            accounts: IDemolitionProfileAccount[]
            site_details: {
                source: IDemolitionProfileSiteDetail
                destination: IDemolitionProfileSiteDetail
            }
        }
        overview: {
            project_id: number
            project_name: string
            display_id: string
            start_date: string | null
            requested_closure_date: string | null
            actual_close_date: string | null
            business_poc: { id: number; name: string } | null
            project_details: {
                template_id: number
                template_name: string
                deal_type: string
                category: ProjectCategory
                created_at: string
                created_by: { id: number; name: string; email: string }
                program: any | null
                msrd_enabled: boolean
            }
            destination_address: IDemolitionProfileSiteDetail | null
            stakeholders: {
                accounts: IDemolitionProfileAccount[]
                site_accounts: {
                    site_id: number
                    site_name: string
                    cpo_id: number
                    host_id: number
                    cobrand_id: number | null
                    is_cobranded: number
                } | null
            }
            manpower: any
            amc: any
            payment_terms: any | null
        }
    }
}

export interface IDemolitionMaterialDetailedItem {
    usage_id: number
    project_product_id: number
    serial_number: string | null
    tracking_type: string | null
    quantity: number
    sku_code: string
    description: string | null
    charger_type?: string | null
    ops_power_rating: number | string | null
    branding: string | null
    insurance: number | null
    operational_status: string | null
    sub_status: string | null
    sub_status_color: string | null
    warranty_criteria: string | null
    warranty_start_date: string | null
    warranty_duration: number | null
    warranty_end_date: string | null
    warranty_status: "Active" | "Expired" | null
    amc_applicable: number | null
    amc_type: string | null
    amc_start_date: string | null
    amc_duration: number | null
    amc_end_date: string | null
    amc_status: "Active" | "Expired" | null
    remarks?: string | null
    decommissioned_date?: string | null
    decommissioning_date?: string | null
}

export interface IDemolitionMaterialSkuItem {
    sku_code: string
    description: string | null
    charger_type: string | null
    count: number
}

export interface IDemolitionMaterialDetailsResponse {
    status: string
    project_id: number
    type: "charger" | "material"
    data: {
        detailed_view: IDemolitionMaterialDetailedItem[]
        sku_view: IDemolitionMaterialSkuItem[]
    }
}

export interface IDemolitionButtonDropdownItem {
    button_status_key: string
    button_status_id: number | null
    button_text: string
    modal: string | null
}

export interface IDemolitionButtonDropdownV3 {
    ticket_id: number
    current_status: string
    on_hold: boolean
    buttons: IDemolitionButtonDropdownItem[]
    finance_alert: {
        alert: boolean
        display_id: string
        finance_ticket_id: number
    } | null
}

export type TDemolitionChargerDetails = {
    charger_id: number
    charger_name: string
    charger_sku: string
    charger_type: string | null
    cin: string | null
    decommissioning_date: string | null
    description: string | null
    is_demolished: boolean
    oem: string | null
    status: string | null
    sub_status: string | null
}

const demolitionService = api.injectEndpoints({
    endpoints: build => ({
        getDemolitionListTabs: build.query<IDemolitionTabCount[], IDemolitionListFilters>({
            query: (filters = {}) => ({
                url: `${MODULE_URL_V3}/list/total`,
                params: filters,
                useV3: true,
            }),
            transformResponse: (res: { message: IDemolitionTabCount[] }) => res.message,
            providesTags: ["Demolition"],
        }),
        getDemolitionList: build.query<
            IDemolitionListResponse,
            { parent_status: DemolitionParentStatus } & IDemolitionListFilters
        >({
            query: ({ parent_status, ...filters }) => ({
                url: `${MODULE_URL_V3}/list/${parent_status}`,
                params: filters,
                useV3: true,
            }),
            providesTags: ["Demolition"],
        }),
        getDemolitionTicketDetails: build.query<any, string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "GET",
                params: { ticket_id },
            }),
            providesTags: ["Demolition", "Survey"],
        }),
        getDemolitionTicketDetailsV3: build.query<IDemolitionTicketDetailsV3, string>({
            query: ticket_id => ({
                url: `${MODULE_URL_V3}/ticket_details`,
                params: { ticket_id },
                useV3: true,
            }),
            transformResponse: (res: any) => res?.data ?? res,
            providesTags: ["Demolition", "Projects"],
        }),

        getDemolitionSalesDetails: build.query<any, string>({
            query: ticket_id => ({
                url: `${MODULE_URL_V3}/sales_details`,
                method: "GET",
                params: { ticket_id },
                useV3: true,
            }),
            providesTags: ["Demolition", "Projects", "Finance"],
        }),
        // Demolition projects are always standalone, so the category is always edited from the
        // ticket — there is no visible project to edit it on.
        updateDemolitionTicketCategory: build.mutation<
            { status: string; ticket_id: number; category: { id: number; name: string } },
            { ticket_id: number | string; category_id: number }
        >({
            query: ({ ticket_id, category_id }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/category`,
                method: "PUT",
                body: { category_id },
                useV3: true,
            }),
            invalidatesTags: ["Demolition"],
        }),
        raiseDemolition: build.mutation<IRaiseDemolitionResponse, IRaiseDemolitionPayload>({
            query: body => ({
                url: `${MODULE_URL_V3}/raise`,
                method: "POST",
                body,
                useV3: true,
            }),
            transformResponse: (res: any) => (res?.message ? res.message : res) as IRaiseDemolitionResponse,
            invalidatesTags: ["Demolition"],
        }),
        getDemolitionSiteMaterials: build.query<
            IDemolitionSiteMaterialsResponse,
            { site_id: number; ticket_id?: string | number }
        >({
            query: ({ site_id, ticket_id }) => ({
                url: `${MODULE_URL_V3}/site/${site_id}/materials`,
                method: "GET",
                useV3: true,
                ...(ticket_id ? { params: { ticket_id } } : {}),
            }),
        }),
        getDemolitionChargerDetails: build.query<TDemolitionChargerDetails[], string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/charger_details`,
                method: "GET",
                params: { ticket_id },
            }),
            providesTags: ["Demolition", "Survey"],
        }),
        getDemolitionDRCreationStatus: build.query<
            any,
            {
                ticket_id: string
                project_id: string
                destination_site_id: string
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/check_demo_dr_creation_status`,
                method: "GET",
                params,
            }),
        }),
        addDemolitionDR: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/delivery_request`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        addReverseDemolitionDR: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/reverse_logistics`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        addDemolitionTicket: build.mutation<
            {
                add_payment_terms: boolean
                finance_id: number
                msg: string
            },
            FormData
        >({
            query: data => ({
                url: `${MODULE_URL}/ticket`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
                try {
                    const res = await queryFulfilled
                    const operationState = (getState() as any).operation
                    const demolitionFormValue = operationState.DemolitionFormValue
                    const selectedValue = operationState.selectedValue
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                    dispatch(getDemolition({ data: demolitionFormValue, page: 1, selectedValue: selectedValue }))
                    dispatch(getDemolitionTabs({ data: demolitionFormValue, page: 1, selectedValue: selectedValue }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition"],
        }),
        editDemolitionTicket: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/ticket`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        putDemolitionOnHoldV3: build.mutation<
            { message: string },
            {
                ticket_id: string
                project_id: number
                on_hold: boolean
                reason_id?: number
                standby?: string
                tentative_date?: string
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/on_hold`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message || "Ticket updated successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition"],
        }),
        editDemolitionTicketStatusV3: build.mutation<
            { message: string },
            {
                ticket_id: string
                new_status_id: number
                contractor_id?: number | null
                executive_id?: number | null
                target_closure_date?: string
                reason_id?: number
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/ticket`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message || "Status updated successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition"],
        }),
        completeDemolitionTicket: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                new_status_id: number
                charger_sub_statuses: { plp_id: number; operational_status: number; decommissioned_date: string }[]
                material_sub_statuses: { plp_id: number; operational_status: number; decommissioned_date: string }[]
                checklist: { item_key: string; is_checked: boolean }[]
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/ticket`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message || "Ticket completed successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition"],
        }),
        editDemolitionChargerDetails: build.mutation<
            { message: string },
            {
                ticket_id: string
                project_id: string
                added: any[]
                edited: any[]
                deleted: { charger_id: number }[]
            }
        >({
            query: data => ({
                url: `${MODULE_URL}/charger_details`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        requestDemolitionSurvey: build.mutation<IResponse<[string]>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/request_survey`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.data?.[0] || "Site survey requested successfully!", { variant: "success" })
                } catch (error: any) {
                    const msg =
                        error?.error?.data?.error ||
                        error?.error?.data?.message ||
                        error?.message ||
                        "Something went wrong!"
                    enqueueSnackbar(msg, { variant: "error" })
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        linkDemolitionSurvey: build.mutation<IResponse<string>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/link_survey`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data || "Site survey linked successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        sendDemolitionAlert: build.mutation<
            {
                message: string
            },
            {
                ticket_id: number
            }
        >({
            query: data => ({
                url: `${MODULE_URL_V3}/send_demolition_alert`,
                method: "POST",
                body: data,
                useV3: true,
            }),
        }),
        addDemolitionDrV2: build.mutation<string, FormData>({
            query: data => ({
                url: `${MODULE_URL}/delivery_request_v2`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data || "Delivery request added successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        addDemolitionDrV3: build.mutation<{ msg: string }, FormData>({
            query: data => ({
                url: "upload_dr_v3",
                method: "POST",
                body: data,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.msg || "Delivery request added successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        getDemolitionOverallProgress: build.query<IDemolitionOverallProgress, string>({
            query: ticket_id => ({
                url: `${MODULE_URL_V3}/overall_progress`,
                params: { ticket_id },
                useV3: true,
            }),
            transformResponse: (response: { data: IDemolitionOverallProgress }) => response.data,
            providesTags: ["Demolition"],
        }),
        getDemolitionProfileOverview: build.query<IDemolitionProfileOverviewResponse, string>({
            query: ticket_id => ({
                url: `${MODULE_URL_V3}/project_profile_overview`,
                params: { ticket_id },
                useV3: true,
            }),
            providesTags: ["Demolition", "Projects"],
        }),

        getDemolitionButtonDropdownV3: build.query<IDemolitionButtonDropdownV3, { ticket_id: string; user_id: number }>(
            {
                query: ({ ticket_id, user_id }) => ({
                    url: `${MODULE_URL_V3}/${ticket_id}/button_dropdown`,
                    params: { user_id },
                    useV3: true,
                }),
                transformResponse: (response: any) => response?.data ?? response,
                providesTags: ["Demolition", "Finance"],
            }
        ),

        getDemolitionContractors: build.query<
            { vendor_id: number; account_name: string }[],
            { search_key?: string; city_id?: number[]; state_id?: number[]; page?: number; per_page?: number }
        >({
            query: params => ({
                url: `${MODULE_URL_V3}/contractors`,
                params: {
                    ...params,
                    city_id: params.city_id ? JSON.stringify(params.city_id) : "",
                    state_id: params.state_id ? JSON.stringify(params.state_id) : "",
                },
                useV3: true,
            }),
            transformResponse: (res: any) => res?.data ?? res,
        }),

        getAssignedDemolitionContractor: build.query<SurveyContractor[], { ticket_id: string | number }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/contractors`,
                useV3: true,
            }),
            transformResponse: (res: any) => (Array.isArray(res) ? res : (res?.data ?? [])),
            providesTags: ["Demolition"],
        }),

        assignDemolitionContractor: build.mutation<
            { message: string; vendor_id: number },
            {
                ticket_id: string | number
                vendor_id: number
                executive_id?: number | null
                deactivate_existing?: boolean
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/contractors`,
                method: "POST",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Contractor assigned successfully!", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Demolition"],
        }),

        editDemolitionContractor: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                vts_id?: number
                is_active?: boolean
                rebuild_vendor_code?: boolean
                deactivate_existing?: boolean
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/contractors`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Demolition"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Contractor updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to update contractor", {
                        variant: "error",
                    })
                }
            },
        }),

        editDemolitionConnectionDetails: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                cancellation_required: 0 | 1
                security_deposit_amount: number | string
                cancellation_status?: string | null
                cancellation_date?: string | null
                refund_status?: string | null
                refund_date?: string | null
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/connection_details`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Demolition"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Connection details updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.message || error?.message || "Failed to update connection details",
                        {
                            variant: "error",
                        }
                    )
                }
            },
        }),

        getDemolitionMaterialDetails: build.query<
            IDemolitionMaterialDetailsResponse,
            {
                project_id: string | number
                type: "charger" | "material"
            }
        >({
            query: ({ project_id, type = "charger" }) => ({
                url: `${MODULE_URL_V3}/project_materials`,
                useV3: true,
                params: { project_id, type },
            }),
            providesTags: ["Demolition", "Projects"],
        }),

        updateDemolitionPlpUsage: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                project_id: number
                ptm_id: number
                plp_ids: number[]
                mapping_type: "charger" | "material"
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticket_id}/plp_usage`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Demolition", "Projects"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Added successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),

        getDemolitionHistory: build.query<HistoryData[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/ticket_history`,
                params: { ticket_id },
            }),
            providesTags: ["Demolition"],
        }),

        downloadDemolitionList: build.query<
            IFile,
            { parent_status: DemolitionParentStatus } & Omit<IDemolitionListFilters, "page" | "per_page">
        >({
            query: ({ parent_status, ...filters }) => ({
                url: `${MODULE_URL_V3}/list/download`,
                params: { parent_status, ...filters },
                useV3: true,
            }),
            keepUnusedDataFor: 0,
        }),
        downloadDemolitionTickets: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download_demolition_tickets`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_ids: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    deal_done_by_id: JSON.stringify(params.data.deal_done_by_id ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    reason_ids: JSON.stringify(params.data.reasons ?? []),
                    site_ids: params?.data?.site_ids?.length
                        ? JSON.stringify(params.data.site_ids.map((i: DropdownObj) => i.id))
                        : "[]",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    vendor_ids: JSON.stringify(params.data.vendor_ids ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by: JSON.stringify(params.data.raised_by_id ?? []),
                    read_filter: params.data.ReadUnread ?? "all",
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    parent_status: params.data.parent_status ?? "all_tab",
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (typeof data === "string" || data?.urls?.download) {
                        const message = typeof data === "string" ? data : "Downloaded successfully"
                        dispatch(
                            setAlertFunc({
                                type: "success",
                                msg: message,
                            })
                        )
                    }
                } catch (error: any) {}
            },
            keepUnusedDataFor: 0,
        }),
        updateDemolitionTentativeDate: build.mutation<
            { message: string },
            { project_id: number; tentative_date: string }
        >({
            query: body => ({
                url: `project_closure_date`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Demolition"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Tentative date updated!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update tentative date", {
                        variant: "error",
                    })
                }
            },
        }),
        requestDemolitionSurveyV3: build.mutation<
            { message: string },
            {
                visit_by_date: string
                time_slot: number
                ticketId: string | number
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL_V3}/${ticketId}/request_survey`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Survey", "Projects", "Demolition"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Survey requested successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),
        getDemoProjectTickets: build.query<any, { projectId: string | number }>({
            query: ({ projectId }) => ({
                url: `${MODULE_URL_V3}/project_tickets`,
                useV3: true,
                params: {
                    project_id: projectId,
                },
            }),
            providesTags: ["Demolition", "Finance"],
        }),
    }),
})

export const {
    useGetDemolitionListTabsQuery,
    useGetDemolitionListQuery,
    useGetDemolitionTicketDetailsQuery,
    useGetDemolitionTicketDetailsV3Query,
    useGetDemolitionChargerDetailsQuery,
    useGetDemolitionOverallProgressQuery,
    useGetDemolitionProfileOverviewQuery,
    useGetDemolitionButtonDropdownV3Query,
    useGetDemolitionContractorsQuery,
    useGetDemolitionHistoryQuery,
    useGetDemolitionSiteMaterialsQuery,
    useLazyGetDemolitionSiteMaterialsQuery,
    useRaiseDemolitionMutation,
    useUpdateDemolitionTicketCategoryMutation,
    useAddDemolitionTicketMutation,
    useAddDemolitionDRMutation,
    useEditDemolitionTicketMutation,
    usePutDemolitionOnHoldV3Mutation,
    useEditDemolitionTicketStatusV3Mutation,
    useCompleteDemolitionTicketMutation,
    useEditDemolitionChargerDetailsMutation,
    useRequestDemolitionSurveyMutation,
    useLinkDemolitionSurveyMutation,
    useSendDemolitionAlertMutation,
    useAddDemolitionDrV2Mutation,
    useAddDemolitionDrV3Mutation,
    useLazyGetDemolitionDRCreationStatusQuery,
    useAddReverseDemolitionDRMutation,
    useLazyDownloadDemolitionTicketsQuery,
    useLazyDownloadDemolitionListQuery,
    useGetDemolitionSalesDetailsQuery,
    useGetAssignedDemolitionContractorQuery,
    useAssignDemolitionContractorMutation,
    useEditDemolitionContractorMutation,
    useEditDemolitionConnectionDetailsMutation,
    useGetDemolitionMaterialDetailsQuery,
    useUpdateDemolitionTentativeDateMutation,
    useGetDemoProjectTicketsQuery,
    useRequestDemolitionSurveyV3Mutation,
    useUpdateDemolitionPlpUsageMutation,
} = demolitionService
